// Scoped admin-panel access.
//
// The capability list comes from the server on the player profile
// (`formatPlayerProfile` → `capabilities`) and is the ONLY source of
// truth. There is deliberately no client-side email allowlist: the
// panel used to carry one, which meant the UI and the server could
// disagree about who was staff, and the client's copy was the one
// people read.
//
// Everything here is presentation. Every action the panel fires is
// re-checked server-side by `requireCap`, so hiding a control is a
// courtesy, not a guard.

export function staffCapabilities(profile) {
  const preview = previewCapabilities(profile);
  if (preview) return preview;
  return Array.isArray(profile?.capabilities) ? profile.capabilities : [];
}

export function hasCap(profile, cap) {
  return staffCapabilities(profile).includes(cap);
}

/** True when the account holds any staff capability at all. Gates the hub button. */
export function hasStaffAccess(profile) {
  return staffCapabilities(profile).length > 0;
}

const ROLE_TITLES = {
  admin: 'Admin Panel',
  moderator: 'Moderator Tools',
  qa: 'QA Tools',
};

/** The role every surface should render as. The preview when one is active. */
export function effectiveStaffRole(profile) {
  return (previewCapabilities(profile) ? previewRole : profile?.staffRole) || null;
}

export function staffPanelTitle(profile) {
  return ROLE_TITLES[effectiveStaffRole(profile)] || 'Admin Panel';
}

// ── Dev role preview ──────────────────────────────────────────────
//
// Lets an owner see the panel as a moderator or QA tester sees it
// without keeping a second account per role. The switcher that drives
// this lives in AppHeader and is gated on isDevServerBuild(), so it
// only ever appears in an unpackaged build pointed at localhost.
//
// The capability lists come from the server (`capabilityMatrix` on the
// profile, admins only) rather than a copy of CAPS kept here. A local
// copy is the thing that would drift and then quietly lie about what a
// moderator can do, which is the one job this feature has.
//
// In-memory and session-scoped on purpose: a reload drops back to the
// real role, so a forgotten preview can never be mistaken for a
// permissions bug. And it is still only presentation. The server
// checks the REAL role on every request, so an owner previewing 'qa'
// can fire an admin-only action and it will succeed.

let previewRole = null;
const previewListeners = new Set();

/** True when the profile came with a capability matrix, i.e. it's an owner. */
export function canPreviewRoles(profile) {
  return !!profile?.capabilityMatrix;
}

export function availablePreviewRoles(profile) {
  return canPreviewRoles(profile) ? Object.keys(profile.capabilityMatrix) : [];
}

export function getRolePreview() {
  return previewRole;
}

/** Pass null to drop back to the account's real role. */
export function setRolePreview(role) {
  previewRole = role || null;
  for (const fn of previewListeners) fn(previewRole);
  return previewRole;
}

export function onRolePreviewChange(fn) {
  previewListeners.add(fn);
  return () => previewListeners.delete(fn);
}

function previewCapabilities(profile) {
  if (!previewRole || !canPreviewRoles(profile)) return null;
  const caps = profile.capabilityMatrix[previewRole];
  return Array.isArray(caps) ? caps : null;
}
